import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Alert,
  Button,
  Table,
  Input,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import {
  buildAllBookingSummaries,
  getTypeBadge,
  type BookingOrderSummary,
} from '../../utils/bookingOrderHelpers';
import BookingStatusSelect from '../../components/Bookings/BookingStatusSelect';
import PaymentStatusSelect from '../../components/Bookings/PaymentStatusSelect';
import VoucherActions from '../../components/Bookings/VoucherActions';
import CustomerCancelRequestButton from '../../components/Orders/CustomerCancelRequestButton';
import { useScopedOrders } from '../../hooks/useScopedOrders';

const BookingsList: React.FC = () => {
  document.title = 'All Bookings | NGK Tours';
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [search, setSearch] = useState('');

  const { orders, loading, error, refetch } = useScopedOrders({ first: 100 });

  const summaries = useMemo(
    () => buildAllBookingSummaries(orders),
    [orders]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summaries;
    return summaries.filter((s) => {
      const haystack = [
        s.orderNo,
        s.customerName,
        s.status,
        s.paymentStatus,
        ...s.types,
        ...s.services.map((x) => x.packageName),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [summaries, search]);

  const toggleExpand = (orderId: number) => {
    setExpanded((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const renderTypeBadges = (summary: BookingOrderSummary) => (
    <div className="d-flex align-items-center flex-wrap gap-1">
      {summary.types.map((type) => {
        const badge = getTypeBadge(type);
        return (
          <Badge key={type} color={badge.color} className={type === 'tour' ? 'text-dark' : ''}>
            {badge.label}
          </Badge>
        );
      })}
      {summary.services.length > 0 && (
        <Button
          color="primary"
          outline
          size="sm"
          className="rounded-circle p-0 ms-1"
          style={{ width: 28, height: 28, lineHeight: '26px' }}
          onClick={() => toggleExpand(summary.order.databaseId)}
          title={expanded[summary.order.databaseId] ? 'Collapse packages' : 'Expand packages'}
        >
          <b>{expanded[summary.order.databaseId] ? '−' : '+'}</b>
        </Button>
      )}
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Bookings" breadcrumbItem="All Bookings" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <p className="text-muted mb-0">
                      Orders with tours, hotels, and transfers. Expand Booking Type to see each package and its price.
                      Staff can change Booking Status and Payment Status here — payment is saved on the order; booking status updates WooCommerce order status.
                    </p>
                    <div className="d-flex gap-2">
                      <Input
                        type="search"
                        placeholder="Search booking..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ minWidth: 220 }}
                      />
                      <Button color="secondary" size="sm" outline onClick={() => refetch()}>
                        Refresh
                      </Button>
                    </div>
                  </div>

                  {error && (
                    <Alert color="danger" className="mb-3">
                      {(error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]
                        ?.message || error.message}
                    </Alert>
                  )}

                  {loading && !summaries.length ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading bookings...</p>
                    </div>
                  ) : filtered.length === 0 ? (
                    <Alert color="info" className="mb-0">
                      No bookings found.
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-bordered table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Order No</th>
                            <th>Customer Name</th>
                            <th>Booking Type</th>
                            <th>Booking Date</th>
                            <th>Tour Start Date</th>
                            <th>Booking Status</th>
                            <th>Amount</th>
                            <th>Payment Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((summary) => {
                            const isOpen = !!expanded[summary.order.databaseId];
                            return (
                              <React.Fragment key={summary.order.databaseId}>
                                <tr>
                                  <td className="fw-medium">{summary.orderNo}</td>
                                  <td>{summary.customerName}</td>
                                  <td>{renderTypeBadges(summary)}</td>
                                  <td>{summary.bookingDate}</td>
                                  <td>{summary.travelStartDate}</td>
                                  <td>
                                    <BookingStatusSelect
                                      orderId={summary.order.databaseId}
                                      status={summary.status}
                                    />
                                  </td>
                                  <td className="fw-medium">{summary.amount}</td>
                                  <td>
                                    <PaymentStatusSelect order={summary.order} />
                                  </td>
                                  <td>
                                    <div className="d-flex flex-wrap align-items-center gap-1">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() =>
                                          navigate(`/bookings/${summary.order.databaseId}`)
                                        }
                                      >
                                        View
                                      </Button>
                                      <VoucherActions
                                        order={summary.order}
                                        mode="download-only"
                                      />
                                      <CustomerCancelRequestButton
                                        orderId={summary.order.databaseId}
                                        status={summary.status}
                                        size="sm"
                                      />
                                    </div>
                                  </td>
                                </tr>
                                {isOpen && (
                                  <tr className="bg-light">
                                    <td colSpan={9} className="p-0">
                                      <Table className="table-bordered mb-0 table-sm">
                                        <thead>
                                          <tr>
                                            <th>Type</th>
                                            <th>Order No</th>
                                            <th>Customer Name</th>
                                            <th>Package / Booking</th>
                                            <th>Booking Date</th>
                                            <th>Travel Date</th>
                                            <th>Status</th>
                                            <th>Amount</th>
                                            <th>Payment</th>
                                            <th>Action</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {summary.services.map((service) => {
                                            const badge = getTypeBadge(service.type);
                                            return (
                                              <tr key={service.subOrderNo}>
                                                <td>
                                                  <Badge
                                                    color={badge.color}
                                                    className={
                                                      service.type === 'tour' ? 'text-dark' : ''
                                                    }
                                                  >
                                                    {badge.label}
                                                  </Badge>
                                                </td>
                                                <td>{service.subOrderNo}</td>
                                                <td>{summary.customerName}</td>
                                                <td>{service.packageName}</td>
                                                <td>{summary.bookingDate}</td>
                                                <td>{service.travelDate}</td>
                                                <td>
                                                  <BookingStatusSelect
                                                    orderId={summary.order.databaseId}
                                                    status={summary.status}
                                                  />
                                                </td>
                                                <td className="fw-medium">{service.amount}</td>
                                                <td>
                                                  <PaymentStatusSelect order={summary.order} />
                                                </td>
                                                <td>
                                                  <Button
                                                    color="link"
                                                    size="sm"
                                                    className="p-0"
                                                    onClick={() =>
                                                      navigate(
                                                        `/bookings/${summary.order.databaseId}`
                                                      )
                                                    }
                                                  >
                                                    View
                                                  </Button>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </Table>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BookingsList;
